import { translateUi } from "@/shared/lib/ui-translate";
import { z } from "zod";
import { goodLuckCoreEntitiesRosterInvitationSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/entities/rosterInvitationSchema";

export const RosterMemberSchema = z.lazy(() =>
  z.object({
  id: z.string().min(1, { error: () => translateUi("litRosterSchemas.idUchastnikaNeMozhetBytPustym") }),
  name: z
    .string()
    .min(3, { error: () => translateUi("litRosterSchemas.imyaUchastnikaDolzhnoSoderzhatMinimum3") })
    .max(30, { error: () => translateUi("litRosterSchemas.imyaUchastnikaNeMozhetBytDlinnee") }),
  role: z.enum(
    ["Top", "Jungle", "Mid", "Adc", "Support", "Substitute", "Coach"],
    { error: () => translateUi("litRosterSchemas.nekorrektnayaRolUchastnika") },
  ),
  avatar: z.string().url({ error: () => translateUi("litRosterSchemas.nekorrektnyyUrlAvatara") }).optional().default(""),
  isOnline: z.boolean().default(false),
  rank: z.string().optional().default(""),
  region: z
    .enum(
      ["EUW", "EUNE", "NA", "BR", "LAN", "LAS", "OCE", "RU", "TR", "JP", "KR"],
      { error: () => translateUi("litRosterSchemas.nekorrektnyyRegion") },
    )
    .optional()
    .default("EUW"),
  isCaptain: z.boolean().optional().default(false),
  userId: z.string().optional(),
  username: z.string().optional(),
  riotGameName: z.string().optional(),
  riotTagLine: z.string().optional(),
  riotRegion: z.string().optional(),
}));

export type TeamMemberDTO = z.infer<typeof RosterMemberSchema>;

export const RosterTournamentSchema = z.lazy(() =>
  z.object({
  name: z
    .string()
    .min(3, { error: () => translateUi("litRosterSchemas.nazvanieTurniraDolzhnoSoderzhatMinimum3") })
    .max(100, { error: () => translateUi("litRosterSchemas.nazvanieTurniraNeMozhetBytDlinnee") }),
  position: z
    .string()
    .regex(/^\d+[а-яё]*$/, { error: () => translateUi("litRosterSchemas.pozitsiyaDolzhnaBytChislomSVozmozhnym") }),
  prize: z
    .string()
    .regex(/^\d+[а-яё]*$/, { error: () => translateUi("litRosterSchemas.prizDolzhenBytChislomSVozmozhnym") }),
  year: z.string().regex(/^\d{4}$/, { error: () => translateUi("litRosterSchemas.godDolzhenBytVFormateYyyy") }),
}));

export const MatchHistorySchema = z.lazy(() =>
  z.object({
  id: z.string(),
  tournamentName: z.string(),
  tournamentId: z.string(),
  round: z.number(),
  result: z.string(),
  opponentName: z.string(),
  myScore: z.number(),
  enemyScore: z.number(),
  date: z.string(),
}));

export const RosterPermissionsSchema = z.lazy(() =>
  z.object({
  canInvite: z.boolean(),
  canKick: z.boolean(),
  canChangeRoles: z.boolean(),
  canChangeCaptain: z.boolean(),
  canEditTeam: z.boolean(),
}));

export const RosterCurrentUserSchema = z.lazy(() =>
  z.object({
  isMember: z.boolean(),
  isCaptain: z.boolean(),
  permissions: RosterPermissionsSchema,
}));

/**
 * @deprecated goodLuckCoreEntitiesRosterSchema (Kubb), путь …/entities/rosterSchema
 */
export const RosterSchema = z.lazy(() =>
  z.object({
  id: z.string().min(1, { error: () => translateUi("litRosterSchemas.idKomandyNeMozhetBytPustym") }),
  name: z
    .string()
    .min(3, { error: () => translateUi("litRosterSchemas.nazvanieKomandyDolzhnoSoderzhatMinimum3") })
    .max(50, { error: () => translateUi("litRosterSchemas.nazvanieKomandyNeMozhetBytDlinnee") }),
  tag: z
    .string()
    .length(3, { error: () => translateUi("litRosterSchemas.tegDolzhenSostoyatRovnoIz3") })
    .regex(
      /^[A-Za-z0-9]+$/,
      { error: () => translateUi("litRosterSchemas.tegMozhetSoderzhatTolkoLatinskieBukvy") },
    ),
  icon: z.string().url({ error: () => translateUi("litRosterSchemas.nekorrektnyyUrlIkonki") }).or(z.literal("")).default(""),
  banner: z
    .string()
    .url({ error: () => translateUi("litRosterSchemas.nekorrektnyyUrlBannera") })
    .or(z.literal(""))
    .default(""),
  game: z.enum(
    ["League of Legends", "Valorant", "CS:GO", "Dota 2", "Overwatch"],
    { error: () => translateUi("litRosterSchemas.nekorrektnayaIgra") },
  ),
  status: z.enum(["active", "archived"]).optional().default("active"),
  tournament: z.string().optional().default(""),
  tournamentId: z.string().optional().default(""),
  captain: z.string().optional().default(""),
  captainId: z.string().default(""),
  coach: z.string().optional().default(""),
  description: z.string().optional(), // Added description field
  isTemporary: z.boolean().optional().default(false),
  isLookingForPartner: z.boolean().optional().default(false),
  acceptScrimInvites: z.boolean().optional().default(false),
  archivedAt: z.string().datetime().optional(),
  tournaments: z.array(RosterTournamentSchema).default([]),
  members: z.array(RosterMemberSchema).default([]),
  position: z
    .string()
    .regex(/^\d+[а-яё]*$/, { error: () => translateUi("litRosterSchemas.pozitsiyaDolzhnaBytChislomSVozmozhnym") })
    .default(""),
  prize: z
    .string()
    .regex(/^\d+[а-яё]*$/, { error: () => translateUi("litRosterSchemas.prizDolzhenBytChislomSVozmozhnym") })
    .default(""),
  invitationToken: z.string().optional(),
  matchHistory: z.array(MatchHistorySchema).default([]),
  currentUser: RosterCurrentUserSchema.optional(),
}));

/**
 * @deprecated z.infer<typeof goodLuckCoreDTOsRosterResponseSchema>
 */
export type TeamDTO = z.infer<typeof RosterSchema>;

// Схемы запросов для создания/обновления команды
export const CreateRosterRequestSchema = z.lazy(() =>
  z.object({
  name: z
    .string()
    .min(3, { error: () => translateUi("litRosterSchemas.nazvanieDolzhnoBytNeMenee3") })
    .max(50, { error: () => translateUi("litRosterSchemas.nazvanieDolzhnoBytNeBolee50") }),
  tag: z
    .string()
    .min(2, { error: () => translateUi("litRosterSchemas.tegDolzhenSoderzhatOt2Do") })
    .max(3, { error: () => translateUi("litRosterSchemas.tegDolzhenSoderzhatOt2Do") })
    .regex(
      /^[A-Za-zА-Яа-яЁё0-9]+$/,
      { error: () => translateUi("litRosterSchemas.tegMozhetSoderzhatTolkoBukvyI") },
    ),
  icon: z.string().url({ error: () => translateUi("litRosterSchemas.nekorrektnyyUrlIkonki") }).optional(),
  banner: z.string().url({ error: () => translateUi("litRosterSchemas.nekorrektnyyUrlBannera") }).optional(),
}));

export const UpdateRosterRequestSchema = z.lazy(() =>
  z.object({
  name: z
    .string()
    .trim()
    .min(3, { error: () => translateUi("litRosterSchemas.nazvanieDolzhnoBytNeMenee3") })
    .max(50, { error: () => translateUi("litRosterSchemas.nazvanieDolzhnoBytNeBolee50") })
    .optional(),
  tag: z
    .string()
    .min(2, { error: () => translateUi("litRosterSchemas.tegDolzhenSoderzhatOt2Do") })
    .max(3, { error: () => translateUi("litRosterSchemas.tegDolzhenSoderzhatOt2Do") })
    .regex(
      /^[A-Za-zА-Яа-яЁё0-9]+$/,
      { error: () => translateUi("litRosterSchemas.tegMozhetSoderzhatTolkoBukvyI") },
    )
    .refine((val) => !["HUI", "PIS", "XYI", "FUK"].includes(val.toUpperCase()), {
      error: () => translateUi("litRosterSchemas.nedopustimyyTeg"),
    })
    .optional(),
  icon: z.string().url({ error: () => translateUi("litRosterSchemas.nekorrektnyyUrlIkonki") }).optional(),
  banner: z.string().url({ error: () => translateUi("litRosterSchemas.nekorrektnyyUrlBannera") }).optional(),
  description: z
    .string()
    .max(500, { error: () => translateUi("litRosterSchemas.opisanieNeMozhetBytDlinnee500") })
    .optional(),
  acceptScrimInvites: z.boolean().optional(),
  coach: z
    .string()
    .min(3, { error: () => translateUi("litRosterSchemas.imyaTreneraDolzhnoSoderzhatMinimum3") })
    .max(30, { error: () => translateUi("litRosterSchemas.imyaTreneraNeMozhetBytDlinnee") })
    .optional(),
}));

export const RosterInvitationSchema = z.lazy(() =>
  z.object({
  id: z.string().min(1, { error: () => translateUi("litRosterSchemas.idPriglasheniyaNeMozhetBytPustym") }),
  teamId: z.string().min(1, { error: () => translateUi("litRosterSchemas.idKomandyNeMozhetBytPustym") }),
  teamName: z
    .string()
    .min(3, { error: () => translateUi("litRosterSchemas.nazvanieKomandyDolzhnoSoderzhatMinimum3") })
    .max(50, { error: () => translateUi("litRosterSchemas.nazvanieKomandyNeMozhetBytDlinnee") }),
  inviterName: z
    .string()
    .min(3, { error: () => translateUi("litRosterSchemas.imyaPriglashayushchegoDolzhnoSoderzhatMinimum3") })
    .max(30, { error: () => translateUi("litRosterSchemas.imyaPriglashayushchegoNeMozhetBytDlinnee") }),
  invitedUserName: z
    .string()
    .min(1, { error: () => translateUi("litRosterSchemas.imyaPriglashennogoNeMozhetBytPustym") })
    .max(50, { error: () => translateUi("litRosterSchemas.imyaPriglashennogoNeMozhetBytDlinnee") })
    .optional(),
  role: z.enum(
    ["Top", "Jungle", "Mid", "Adc", "Support", "Substitute", "Coach"],
    { error: () => translateUi("litRosterSchemas.nekorrektnayaRol") },
  ),
  message: z
    .string()
    .max(200, { error: () => translateUi("litRosterSchemas.soobshchenieNeMozhetBytDlinnee200") })
    .optional(),
  createdAt: z.string().datetime({ error: () => translateUi("litRosterSchemas.nekorrektnayaDataSozdaniya") }).optional(),
  expiresAt: z.string().datetime({ error: () => translateUi("litRosterSchemas.nekorrektnayaDataIstecheniya") }).optional(),
  status: z
    .enum(["pending", "accepted", "declined", "expired"])
    .default("pending"),
}));

export type TeamInvitationDTO = z.infer<typeof RosterInvitationSchema>;

export const RosterInviteRequestSchema = z.lazy(() =>
  z.object({
  username: z.string().min(1, { error: () => translateUi("litRosterSchemas.imyaPolzovatelyaNeMozhetBytPustym") }),
  role: z.enum(
    ["Top", "Jungle", "Mid", "Adc", "Support", "Substitute", "Coach"],
    { error: () => translateUi("litRosterSchemas.nekorrektnayaRol") },
  ),
}));

export const RosterInviteResponseSchema = z.lazy(() =>
  z.object({
  success: z.boolean(),
  invitation: goodLuckCoreEntitiesRosterInvitationSchema.optional(),
  message: z
    .string()
    .max(200, { error: () => translateUi("litRosterSchemas.soobshchenieNeMozhetBytDlinnee200") })
    .optional(),
}));
